// =====================================================
// MetodologiaInforme.js (ACTUALIZADO)
// Panel de ayuda metodológica para el Informe Agropecuario
// Fórmulas alineadas con el motor de cálculo real (archivo 29)
// src/pages/Agro/Productor/MetodologiaInforme.js
// =====================================================

import React, { useState } from "react"
import { Card, CardBody, CardHeader, Badge, Collapse, Button } from "reactstrap"
import SiembrasVigentesNota from "./SiembrasVigentesNota"

// Paleta unificada (misma que archivos 30/31)
const P = {
  verde: { text: "#1a7431", border: "#34a853" },
  ambar: { text: "#854d0e", border: "#f59e0b" },
  marron: { text: "#78350f", border: "#a16207" },
  teal: { text: "#115e59", border: "#0d9488" },
  violeta: { text: "#5b21b6", border: "#8b5cf6" },
  azul: { text: "#1e40af", border: "#3b82f6" },
  rojo: { text: "#7f1d1d", border: "#b91c1c" },
  gris: { text: "#334155", border: "#64748b" },
}

const MetodologiaInforme = () => {
  const [show, setShow] = useState(false)

  return (
    <div className="mb-3 text-muted">
      <Button
        color="light"
        size="sm"
        outline
        onClick={() => setShow(!show)}
        className="border"
      >
        <i className="mdi mdi-help-circle-outline me-1" />
        {show ? "Ocultar metodología" : "¿Cómo se calcula el informe?"}
      </Button>

      <Collapse isOpen={show}>
        <Card className="mt-2 mb-0 border-0 shadow-sm overflow-hidden">
          <div style={{ height: "3px", background: P.verde.border }} />
          <CardHeader className="bg-transparent py-2">
            <span className="fw-semibold">
              Metodología del informe agropecuario
            </span>
            <small className="text-muted d-block">
              Todos los valores de referencia se configuran en{" "}
              <strong>Administración</strong> (Valores Agro, Ganadería,
              Generales y Proyección Crediticia)
            </small>
          </CardHeader>
          <CardBody style={{ fontSize: "0.88rem" }}>
            <SiembrasVigentesNota />
            {/* 1. AGRÍCOLA */}
            <Seccion
              titulo="1. Actividad agrícola"
              color={P.ambar.text}
              border={P.ambar.border}
            >
              <p>
                Para cada siembra activa del productor, se buscan los valores de
                referencia por <strong>zona + cultivo</strong> en la tabla
                Valores Agrícolas:
              </p>
              <Formula>
                <strong>Producción (tn)</strong> = Superficie (ha) × Rendimiento
                (qq/ha) ÷ 10{"\n"}
                <strong>Ingreso bruto</strong> = Producción (tn) × Precio a
                cosecha (USD/tn)
              </Formula>
              <p className="mb-1">
                El costo total (campo «Costo total» de Valores Agro, que ya
                incluye las 5 partidas y no se suman dos veces) se compone de 5
                partidas, todas configurables por zona y cultivo:
              </p>
              <Formula>
                <strong>Costo total</strong> = Superficie × ({"\n"}
                {"  "}Costos directos (USD/ha){"\n"}
                {"  "}+ Gastos de comercialización (USD/ha){"\n"}
                {"  "}+ Arrendamientos (USD/ha){"\n"}
                {"  "}+ Administración de siembra (USD/ha){"\n"}
                {"  "}+ Seguro contra granizo (USD/ha){"\n"}){"\n\n"}
                <strong>Margen bruto</strong> = Ingreso bruto − Costo total
              </Formula>
              <Referencia>
                Administración &gt; Valores Agro (por zona y cultivo). Si el
                productor declaró rendimiento propio, se usa ese en vez del de
                la zona.
              </Referencia>
            </Seccion>

            {/* 2. GANADERÍA */}
            <Seccion
              titulo="2. Actividad ganadera"
              color={P.marron.text}
              border={P.marron.border}
            >
              <p>
                La valuación del rodeo y el ingreso se calculan por categoría de
                hacienda:
              </p>
              <Formula>
                <strong>Valor por cabeza</strong> = Peso Promedio (kg) × Precio
                por kg (USD){"\n"}
                <strong>Valor stock</strong> = Σ (Cabezas × Valor por cabeza)
                por categoría{"\n\n"}
                <strong>Ingreso bruto por ventas</strong> = Cabezas vendidas ×
                Valor por cabeza{"\n"}
                <strong>Gastos de venta</strong> = Ingreso bruto × % gastos
                venta{"\n"}
                <strong>Ingreso neto</strong> = Ingreso bruto − Gastos de venta
                {"\n\n"}
                <strong>Costos ganaderos</strong> = Σ (Cabezas × (Gastos
                productivos + Gastos cría)){"\n"}
                <strong>Margen ganadero</strong> = Ingreso neto − Costos
                ganaderos
              </Formula>
              <p
                className="mb-0"
                style={{ fontSize: "0.82rem", color: P.gris.text }}
              >
                Si no hay ventas declaradas, se estima un 40% de venta anual
                sobre el stock de invernada.
              </p>
              <Referencia>
                Administración &gt; Valores Ganadería (peso, precio/kg, gastos
                venta %, gastos productivos, gastos cría por categoría).
              </Referencia>
            </Seccion>

            {/* 3. TAMBO */}
            <Seccion
              titulo="3. Actividad tambo"
              color={P.teal.text}
              border={P.teal.border}
            >
              <p>El cálculo del tambo usa 6 parámetros configurables:</p>
              <Formula>
                <strong>Producción anual</strong> = Vacas ordeñe × Lts/día × 365
                {"\n"}
                <strong>Ingreso bruto</strong> = Producción anual × Precio litro
                leche{"\n\n"}
                Costos:{"\n"}
                {"  "}
                <strong>Tambero</strong> = Ingreso bruto × % tambero{"\n"}
                {"  "}
                <strong>Mantenimiento</strong> = Vacas ordeñe × Gasto anual por
                vaca{"\n"}
                {"  "}
                <strong>Reposición</strong> = Vacas × % reposición × Precio
                vaquillona{"\n"}
                {"  "}
                <strong>Compra</strong> = Vacas × % reposición × Costo de compra
                {"\n\n"}
                <strong>Margen tambo</strong> = Ingreso − (Tambero +
                Mantenimiento + Reposición + Compra)
              </Formula>
              <Referencia>
                Administración &gt; Valores Generales (% tambero, precio litro
                leche, gasto anual por vaca, % reposición, precio vaquillona,
                costo de compra).
              </Referencia>
            </Seccion>

            {/* 4. CONSOLIDADO */}
            <Seccion
              titulo="4. Indicadores consolidados"
              color={P.violeta.text}
              border={P.violeta.border}
            >
              <Formula>
                <strong>Ingreso total</strong> = Ingreso agrícola + Ingreso
                ganadero + Ingreso tambo{"\n"}
                <strong>Costos totales</strong> = Costos agrícolas + Costos
                ganaderos + Costos tambo{"\n"}
                <strong>Margen neto</strong> = Ingreso total − Costos totales
                {"\n"}
                <strong>Rentabilidad</strong> = (Margen neto ÷ Ingreso total) ×
                100{"\n"}
                <strong>Capacidad de pago</strong> = Margen neto × Factor
                capacidad (configurable)
              </Formula>
              <Referencia>
                El factor de capacidad de pago (defecto 70%) se configura en
                Administración &gt; Proyección Crediticia &gt; Capacidad de
                pago.
              </Referencia>
            </Seccion>

            {/* 5. PROYECCIÓN CREDITICIA */}
            <Seccion
              titulo="5. Proyección crediticia"
              color={P.azul.text}
              border={P.azul.border}
            >
              <p>
                Si el productor tiene siembras activas, el sistema genera
                automáticamente una proyección a 12 meses que incluye estimación
                de cosecha, flujo de caja mensual y dictamen crediticio.
              </p>
              <p className="mb-1">El dictamen puede ser:</p>
              <div className="d-flex gap-2 mb-2 flex-wrap">
                <DictamenBadge tipo="FAVORABLE" desc="Respalda el crédito" />
                <DictamenBadge
                  tipo="CONDICIONAL"
                  desc="Monto excede capacidad"
                />
                <DictamenBadge
                  tipo="DESFAVORABLE"
                  desc="Sin margen suficiente"
                />
              </div>
              <p
                className="mb-0"
                style={{ fontSize: "0.82rem", color: P.gris.text }}
              >
                El rendimiento se ajusta por NDVI satelital (Sentinel-2).
                Incluye monto máximo sugerido, plazo alineado con cosecha y
                garantía propuesta.
              </p>
              <Referencia>
                Administración &gt; Proyección Crediticia (factores NDVI,
                capacidad pago, costos por cultivo, parámetros tambo, dictamen).
              </Referencia>
            </Seccion>

            {/* 6. SCORING */}
            <Seccion
              titulo="6. Scoring agropecuario"
              color={P.verde.text}
              border={P.verde.border}
            >
              <p className="mb-1">
                Score de 0 a 100 puntos, promedio ponderado de siete
                dimensiones:
              </p>
              <div style={{ fontSize: "0.82rem" }} className="mb-2">
                Historial productivo <strong>20%</strong> · Escala productiva{" "}
                <strong>15%</strong> · Condición actual <strong>15%</strong> ·
                Riesgo climático <strong>15%</strong> · Rentabilidad{" "}
                <strong>15%</strong> · Cobertura de riesgos <strong>10%</strong>{" "}
                · Tenencia de tierra <strong>10%</strong>
              </div>
              <p className="mb-1">
                Cada dimensión puede emitir alertas. Los umbrales que las
                disparan no están en el código: se configuran por perfil
                productivo, zona y cultivo, y el informe declara cuál se aplicó.
                Hay dos perfiles — <strong>General</strong> para agricultura
                extensiva y <strong>Cuyo perenne</strong> para vid, olivo y
                frutales bajo riego, que se resuelve por la provincia del
                establecimiento.
              </p>
              <p className="mb-1">Clasificación por categoría de riesgo:</p>
              <div className="d-flex gap-2 mb-0 flex-wrap">
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#f0faf4",
                    color: P.verde.text,
                    border: `1px solid ${P.verde.border}40`,
                  }}
                >
                  A — Excelente
                </span>
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#eff6ff",
                    color: P.azul.text,
                    border: `1px solid ${P.azul.border}40`,
                  }}
                >
                  B — Bueno
                </span>
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#fefce8",
                    color: P.ambar.text,
                    border: `1px solid ${P.ambar.border}40`,
                  }}
                >
                  C — Regular
                </span>
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#fef2f2",
                    color: P.rojo.text,
                    border: `1px solid ${P.rojo.border}40`,
                  }}
                >
                  D — Observado
                </span>
                {/* La categoria E existe en el modelo (score menor a 35) y
                    faltaba en este listado. */}
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#fef2f2",
                    color: P.rojo.text,
                    border: `1px solid ${P.rojo.border}40`,
                  }}
                >
                  E — Crítico
                </span>
              </div>
              <div className="mt-1" style={{ fontSize: "0.78rem" }}>
                Cortes: A desde 80, B desde 65, C desde 50, D desde 35, E por
                debajo de 35.
              </div>
            </Seccion>

            {/* 7. COMPARATIVO + HISTORIAL + TENENCIA */}
            <Seccion
              titulo="7. Análisis complementarios"
              color={P.gris.text}
              border={P.gris.border}
              sinBorde
            >
              <p className="mb-2">
                <strong>Comparativo con zona:</strong> rendimiento del productor
                vs promedio de la zona para cada cultivo (datos de Valores
                Agrícolas).
              </p>
              <p className="mb-2">
                <strong>Historial de campañas:</strong> superficie, rendimiento
                esperado/real y resultado por cultivo en las últimas campañas
                registradas.
              </p>
              <p className="mb-2">
                <strong>Tenencia de tierra:</strong> superficie total,
                distribución propio/arrendado por establecimiento (datos de
                parcelas).
              </p>
              <p className="mb-0">
                <strong>Origen de datos:</strong> el informe puede generarse
                desde el sistema EWAN o importando un Excel (hojas Caratula,
                Cultivo, Ganaderia, Tambo). En ambos casos los valores de
                referencia se toman de la configuración en Administración.
              </p>
            </Seccion>
          </CardBody>
        </Card>
      </Collapse>
    </div>
  )
}

// Sub-componentes

const Seccion = ({ titulo, color, border, children, sinBorde }) => (
  <div
    className={sinBorde ? "mb-2" : "mb-3 pb-3"}
    style={sinBorde ? {} : { borderBottom: "1px solid #f1f5f9" }}
  >
    <div className="d-flex align-items-center gap-2 mb-2">
      <div
        style={{
          width: 3,
          height: 16,
          borderRadius: 2,
          backgroundColor: border || color,
        }}
      />
      <h6 style={{ color, marginBottom: 0 }}>{titulo}</h6>
    </div>
    {children}
  </div>
)

const Formula = ({ children }) => (
  <div
    className="rounded p-2 mb-2"
    style={{
      fontFamily: "monospace",
      fontSize: "0.82rem",
      whiteSpace: "pre-line",
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
    }}
  >
    {children}
  </div>
)

const Referencia = ({ children }) => (
  <div className="mt-1 d-flex align-items-start gap-1">
    <i
      className="mdi mdi-cog-outline"
      style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}
    />
    <small style={{ fontSize: "0.75rem", color: "#64748b" }}>{children}</small>
  </div>
)

const DictamenBadge = ({ tipo, desc }) => {
  const estilos = {
    FAVORABLE: { bg: "#f0faf4", color: "#1a7431", border: "#34a85340" },
    CONDICIONAL: { bg: "#fef9ee", color: "#92400e", border: "#f59e0b40" },
    DESFAVORABLE: { bg: "#fef2f2", color: "#7f1d1d", border: "#b91c1c40" },
  }
  const e = estilos[tipo]
  return (
    <span
      className="px-2 py-1 rounded"
      style={{
        fontSize: "0.72rem",
        backgroundColor: e.bg,
        color: e.color,
        border: `1px solid ${e.border}`,
      }}
    >
      <strong>{tipo}</strong> — {desc}
    </span>
  )
}

export default MetodologiaInforme
